import dayjs from 'dayjs'
import React, { useState, useMemo } from 'react'
import { useLingui } from '@lingui/react'
import { t, Trans } from '@lingui/macro'

import { Typography, Box, Stack, Chip, List, ListItem, ListItemText, ListItemIcon, InputAdornment, CircularProgress, alpha, Button } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventBusyIcon from '@mui/icons-material/EventBusy'

import TextInput from '@/components/ui/inputs/TextInput'
import { Planning } from '@/core/models/planning.model'
import { getPlanningStatus } from '@/shared/plannings/lib/planningSelection'

import { ViewModeToggler } from './ViewModeToggler'
import { PlanningViewMode } from '../model/types'
import { CreateShiftTemplateDialog } from './CreateShiftTemplateDialog'

type PlanningSelectorProps = {
  plannings: Planning[]
  onSelectPlanning: (planning: Planning) => void
  selectedPlanning?: Planning
  onCreateTemplate?: () => void
  calendarViewMode?: PlanningViewMode
  onCalendarViewModeChange?: (mode: PlanningViewMode) => void
  showSelectedPlanningActions?: boolean
}

export function PlanningSelector({
  plannings,
  onSelectPlanning,
  selectedPlanning,
  calendarViewMode = 'all',
  onCalendarViewModeChange,
  showSelectedPlanningActions = true,
}: PlanningSelectorProps) {
  useLingui()

  const [viewMode, setViewMode] = useState<PlanningViewMode>('all')
  const [searchTerm, setSearchTerm] = useState('')

  // Filter plannings based on search term and view mode
  const filteredPlannings = useMemo(() => {
    return plannings.filter((planning) => {
      let match = planning.name.toLowerCase().includes(searchTerm.toLowerCase())
      switch (viewMode) {
        case 'active':
          match = match && getPlanningStatus(planning) === 'active'
          break
        case 'upcoming':
          match = match && getPlanningStatus(planning) === 'upcoming'
          break
        case 'past':
          match = match && getPlanningStatus(planning) === 'past'
          break
        default:
          break
      }
      return match
    })
  }, [plannings, searchTerm, viewMode])

  return (
    <Box>
      {/* Planning list filters */}
      <Stack direction="column" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <ViewModeToggler viewMode={viewMode} onChange={setViewMode} toggleType="planning-filter" />
        <TextInput
          fullWidth
          placeholder={t`Search`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="end">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Stack>

      {/* Planning list */}
      {filteredPlannings.length === 0 ? (
        <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
          <Trans>No plannings found matching your criteria</Trans>
        </Typography>
      ) : (
        <PlanningsList onSelectPlanning={onSelectPlanning} plannings={filteredPlannings} selectedPlanning={selectedPlanning} />
      )}

      {/* Calendar view mode toggle and Create Template button */}
      {selectedPlanning && showSelectedPlanningActions && (
        <Stack spacing={2} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          {onCalendarViewModeChange && <ViewModeToggler viewMode={calendarViewMode} onChange={onCalendarViewModeChange} toggleType="calendar-view" />}
          <CreateShiftTemplateDialog planningId={selectedPlanning._id} />
        </Stack>
      )}
    </Box>
  )
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'active':
      return <EventAvailableIcon color="success" />
    case 'upcoming':
      return <CalendarTodayIcon color="info" />
    case 'past':
      return <EventBusyIcon color="action" />
    default:
      return <CalendarTodayIcon color="primary" />
  }
}

// getPlanningStatus returns lowercase keys; map them to translatable labels.
// (The previous i18n._(status) looked up 'active'/'past'/'upcoming', which are not
// catalog msgids, so the chip rendered the raw lowercase English in every language.)
function getStatusLabel(status: string) {
  switch (status) {
    case 'active':
      return t`Active`
    case 'upcoming':
      return t`Upcoming`
    case 'past':
      return t`Past`
    default:
      return status
  }
}

function PlanningsList({
  onSelectPlanning,
  plannings,
  selectedPlanning,
}: {
  onSelectPlanning: (planning: Planning) => void
  plannings: Planning[]
  selectedPlanning?: Planning
}) {
  return (
    <List
      sx={{
        maxHeight: '100%',
        overflow: 'auto',
        '& .MuiListItem-root': {
          borderRadius: 1,
          mb: 0.5,
        },
      }}
      dense
    >
      {plannings.map((planning) => {
        const status = getPlanningStatus(planning)
        const isSelected = selectedPlanning?._id === planning._id

        return (
          <ListItem
            key={planning._id}
            button
            onClick={() => onSelectPlanning(planning)}
            selected={isSelected}
            sx={{
              bgcolor: isSelected ? (theme) => alpha(theme.palette.primary.main, 0.1) : 'transparent',
              borderLeft: isSelected ? 3 : 0,
              borderColor: 'primary.main',
              pl: isSelected ? 1.5 : 2,
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{getStatusIcon(status)}</ListItemIcon>
            <ListItemText
              primary={planning.name}
              secondary={
                <React.Fragment>
                  <Typography variant="caption" component="span" display="block">
                    {dayjs(planning.startDate).format('MMM D')} - {dayjs(planning.endDate).format('MMM D, YYYY')}
                  </Typography>
                  {planning.location && (
                    <Typography variant="caption" component="span" display="block" noWrap>
                      {planning.location}
                    </Typography>
                  )}
                </React.Fragment>
              }
              primaryTypographyProps={{
                fontWeight: isSelected ? 'bold' : 'normal',
                noWrap: true,
              }}
            />
            <Chip
              label={getStatusLabel(status)}
              size="small"
              color={status === 'active' ? 'success' : status === 'upcoming' ? 'info' : 'default'}
              variant="outlined"
              sx={{ ml: 1 }}
            />
          </ListItem>
        )
      })}
    </List>
  )
}
